import React, { useState } from 'react';
import axios from 'axios';
import VowsForm from './VowsForm';

const buildAbsolutePdfUrl = (pdfUrl) => {
  if (!pdfUrl) return null;
  if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) {
    return pdfUrl;
  }
  const prefix = pdfUrl.startsWith('/') ? '' : '/';
  return `${window.location.origin}${prefix}${pdfUrl}`;
};

const Redeem = () => {
  const [step, setStep] = useState('enter-code');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redemptionInfo, setRedemptionInfo] = useState(null);
  const [vow, setVow] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [satisfactionRating, setSatisfactionRating] = useState(null);
  const [savedFormData, setSavedFormData] = useState(null);
  const [purchaseId, setPurchaseId] = useState(null);
  const [isRetry, setIsRetry] = useState(false);
  const [existingVowId, setExistingVowId] = useState(null);

  const handleDownloadPdf = (pdfUrl) => {
    const absoluteUrl = buildAbsolutePdfUrl(pdfUrl);
    if (!absoluteUrl) return;
    const downloadWindow = window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
    if (downloadWindow) {
      downloadWindow.focus();
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/redeem/validate', { code: code.toUpperCase() });
      
      if (response.data.valid) {
        setPurchaseId(response.data.purchaseId);
        setStep('show-form');
      } else if (response.data.alreadyRedeemed) {
        setRedemptionInfo(response.data);
        if (response.data.canAccess) {
          const vowData = response.data.vow;
          setVow(vowData);
          if (vowData.form_data) {
            setSavedFormData(vowData.form_data);
          }
          if (vowData.purchase_id) {
            setPurchaseId(vowData.purchase_id);
          }
          setStep('show-vow');
        } else {
          setStep('already-redeemed');
        }
      }
    } catch (err) {
      if (err.response?.status === 429 && err.response?.data?.rateLimited) {
        setRateLimitInfo({
          message: err.response.data.error,
          remainingMinutes: err.response.data.remainingMinutes
        });
        setStep('rate-limited');
      } else {
        setError(err.response?.data?.error || 'Invalid code. Please check and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVowSuccess = (vowData) => {
    setVow(vowData);
    if (vowData.form_data) {
      setSavedFormData(vowData.form_data);
    }
    if (vowData.purchase_id) {
      setPurchaseId(vowData.purchase_id);
    }
    if ((vowData.retry_count || 0) > 0) {
      setSatisfactionRating(null);
    } else if (vowData.satisfaction_rating) {
      setSatisfactionRating(vowData.satisfaction_rating);
    }
    setIsRetry(false);
    setExistingVowId(null);
    setStep('show-vow');
  };

  const handleSatisfactionSelect = async (rating) => {
    setSatisfactionRating(rating);
    
    if (vow?.id) {
      try {
        await axios.post('/api/satisfaction', {
          vowId: vow.id,
          rating: rating
        });
        setVow(prev => ({
          ...prev,
          satisfaction_rating: rating
        }));
      } catch (err) {
        console.error('Error saving satisfaction rating:', err);
      }
    }
    
    if (rating !== 'happy') {
      const retryCount = vow?.retry_count || 0;
      
      if (retryCount === 0) {
        if (vow?.id) {
          setExistingVowId(vow.id);
        }
        setIsRetry(true);
        setStep('show-form');
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      }
    }
  };

  const handleVowError = (errorMessage) => {
    setError(errorMessage);
  };

  // Render enter code form
  if (step === 'enter-code') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="glass-card text-center mb-4">
              <h1 className="commercial-heading mb-4">Redeem Your Code</h1>
              <p className="commercial-subheading mb-4">
                Enter your redemption code to create your wedding vows
              </p>
              
              <form onSubmit={handleCodeSubmit}>
                <div className="mb-4">
                  <input
                    type="text"
                    className="form-control form-control-lg text-center"
                    placeholder="VOW-XXXX-XXXX-XXXX"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    maxLength="18"
                    required
                    style={{ letterSpacing: '2px', fontFamily: 'monospace' }}
                  />
                </div>
                
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                
                <button
                  type="submit"
                  className="btn btn-primary btn-lg w-100"
                  disabled={loading || code.length < 18}
                >
                  {loading ? 'Validating...' : 'Continue'}
                </button>
              </form>
            </div>

            <div className="glass-card text-center">
              <h2 className="commercial-heading mb-3" style={{ fontSize: '1.4rem' }}>
                Don't have a code?
              </h2>
              <p className="mb-3" style={{ color: '#5d3a3b' }}>
                Purchase a package to receive your unique redemption codes instantly.
              </p>
              <a
                href="/?checkout=true"
                className="btn btn-primary"
              >
                <i className="bi bi-heart-fill me-2"></i>
                Get Your Codes
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render form
  if (step === 'show-form') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-8">
            <div className="glass-card text-center mb-4">
              <h1 className="commercial-heading mb-3">Create Your Vows</h1>
              <p className="commercial-subheading mb-0">
                Share your love story and we'll craft beautiful vows
              </p>
              <p className="mt-2">
                <span className="badge" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1a365d 100%)', color: 'white' }}>Code: {code}</span>
              </p>
            </div>

            {isRetry && (
              <div className="glass-card mb-4 text-center" style={{ background: 'rgba(232, 164, 174, 0.15)', border: '2px solid rgba(232, 164, 174, 0.4)' }}>
                <h3 className="mb-3" style={{ color: '#8b5a5b' }}>
                  <i className="bi bi-heart me-2"></i>
                  Let's Perfect Your Vows!
                </h3>
                <p className="mb-0" style={{ color: '#5d3a3b' }}>
                  We want to make sure your vows are exactly right. Your form has been pre-filled with your previous responses. 
                  Please make any changes you'd like, and we'll generate new vows that will replace the previous ones.
                </p>
              </div>
            )}

            <VowsForm
              code={code}
              onSuccess={handleVowSuccess}
              onError={handleVowError}
              loading={loading}
              initialFormData={savedFormData}
              isRetry={isRetry}
              existingVowId={existingVowId}
            />
          </div>
        </div>
      </div>
    );
  }

  // Render rate limited message
  if (step === 'rate-limited') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="glass-card text-center">
              <div className="mb-4">
                <i className="bi bi-shield-exclamation" style={{ fontSize: '4rem', color: '#dc3545' }}></i>
              </div>
              <h2 className="commercial-heading mb-3">Too Many Attempts</h2>
              <p className="commercial-subheading mb-4">
                {rateLimitInfo?.message || 'Too many failed attempts. Please try again later.'}
              </p>
              <div className="alert alert-warning" role="alert">
                <i className="bi bi-clock me-2"></i>
                For security purposes, code validation has been temporarily disabled.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render already redeemed message
  if (step === 'already-redeemed') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="glass-card text-center">
              <div className="mb-4">
                <i className="bi bi-exclamation-circle" style={{ fontSize: '4rem', color: '#1e3a5f' }}></i>
              </div>
              <h2 className="commercial-heading mb-3">Code Already Redeemed</h2>
              <p className="commercial-subheading mb-4">
                This code was redeemed on {new Date(redemptionInfo.redeemedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <p>{redemptionInfo.message}</p>
              <button
                className="btn btn-primary mt-4"
                onClick={() => {
                  setStep('enter-code');
                  setCode('');
                  setError('');
                  setRedemptionInfo(null);
                }}
              >
                Try Another Code
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render vow
  if (step === 'show-vow') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-10 col-xl-8">
            <div className="glass-card text-center mb-4">
              <div className="mb-3">
                <i className="bi bi-heart-fill" style={{ fontSize: '4rem', color: '#1e3a5f' }}></i>
              </div>
              <h1 className="commercial-heading mb-3">Your Vows Are Ready!</h1>
              <p className="commercial-subheading">
                Beautiful, heartfelt vows created just for you
              </p>
            </div>

            <div className="glass-card">
              <div className="text-center mb-4">
                <h2 style={{ color: '#5d3a3b', fontFamily: "'Cormorant Garamond', serif" }}>
                  {vow.your_name}'s Vows to {vow.partner_name}
                </h2>
                {vow.wedding_date && (
                  <p style={{ color: '#8b5a5b', fontStyle: 'italic', fontSize: '1.1rem' }}>
                    {new Date(vow.wedding_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                )}
              </div>
              
              <div style={{ 
                color: '#5d3a3b', 
                whiteSpace: 'pre-wrap', 
                textAlign: 'justify', 
                lineHeight: '1.8',
                fontFamily: "'Lora', serif",
                padding: '1.5rem',
                background: 'rgba(30, 58, 95, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(30, 58, 95, 0.2)'
              }}>
                {vow.full_text}
              </div>

              <div 
                className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3 flex-wrap mt-5 pt-4 text-center"
                style={{ borderTop: '1px solid rgba(30, 58, 95, 0.3)' }}
              >
                {vow.pdf_available !== false && (
                  <button
                    type="button"
                    className="btn btn-primary btn-lg px-4"
                    onClick={() => handleDownloadPdf(vow.pdf_url)}
                  >
                    <i className="bi bi-download me-2"></i>
                    Download PDF
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-lg px-4"
                  style={{ color: '#1e3a5f' }}
                  onClick={() => {
                    setStep('enter-code');
                    setCode('');
                    setError('');
                    setVow(null);
                  }}
                >
                  Create Another
                </button>
              </div>

              {/* Satisfaction Survey */}
              {!satisfactionRating && (
                <div className="text-center mt-5 pt-4" style={{ borderTop: '1px solid rgba(30, 58, 95, 0.3)' }}>
                  <h3 className="mb-3" style={{ color: '#5d3a3b', fontSize: '1.3rem', fontFamily: "'Cormorant Garamond', serif" }}>
                    How did we do?
                  </h3>
                  <div className="d-flex justify-content-center gap-4 mb-3">
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0"
                      onClick={() => handleSatisfactionSelect('sad')}
                      style={{ fontSize: '3rem', textDecoration: 'none', background: 'transparent' }}
                      title="Needs work"
                    >
                      😢
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0"
                      onClick={() => handleSatisfactionSelect('indifferent')}
                      style={{ fontSize: '3rem', textDecoration: 'none', background: 'transparent' }}
                      title="It's okay"
                    >
                      😐
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0 border-0"
                      onClick={() => handleSatisfactionSelect('happy')}
                      style={{ fontSize: '3rem', textDecoration: 'none', background: 'transparent' }}
                      title="Love it!"
                    >
                      😍
                    </button>
                  </div>
                </div>
              )}

              {/* Already Rated */}
              {satisfactionRating && (
                <div className="text-center mt-5 pt-4" style={{ borderTop: '1px solid rgba(30, 58, 95, 0.3)' }}>
                  {satisfactionRating === 'happy' ? (
                    <p style={{ color: '#1e3a5f', fontSize: '1.1rem', fontWeight: '600' }}>
                      <i className="bi bi-heart-fill me-2"></i>
                      We're so happy you love your vows! Wishing you a beautiful wedding!
                    </p>
                  ) : (
                    <p style={{ color: '#8b5a5b', fontSize: '0.95rem' }}>
                      Thank you for your feedback. You can use another code to try with different details.
                    </p>
                  )}
                </div>
              )}

              <div className="text-center mt-4" style={{ fontSize: '0.9rem', color: '#8b5a5b' }}>
                <p>
                  <i className="bi bi-info-circle me-2"></i>
                  Your vows and PDF will be accessible for 90 days
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Redeem;
